import type { Camera } from './types'
import config from '../../../viewer.config.json'
import { parseViewerConfig, type ViewerConfig } from '@vod/core/viewer-config'

export type CameraConfig = ViewerConfig
export type PlayerCamera = Camera & {
  sourceIds: string[]
  audioSourceIds?: string[]
  view?: 'quad' | 'audio'
}
const defaultConfig = parseViewerConfig(config)

export const viewerPresetNames = Object.keys(defaultConfig.presets)

export function resolvePreset(
  name?: string | null,
  settings: CameraConfig = defaultConfig,
): string {
  return name && Object.hasOwn(settings.presets, name) ? name : settings.defaultPreset
}

export function presetForUrl(url: URL, settings: CameraConfig = defaultConfig): string {
  const name =
    url.searchParams.get('preset') ??
    (url.pathname.replace(/\/$/, '') === '/demo' ? 'demo' : undefined)
  return resolvePreset(name, settings)
}

export function setPresetForUrl(
  url: URL,
  name?: string,
  settings: CameraConfig = defaultConfig,
): void {
  const preset = resolvePreset(name, settings)
  if (preset === settings.defaultPreset) {
    url.searchParams.delete('preset')
    // Removing an explicit default override must not activate the demo alias.
    if (url.pathname.replace(/\/$/, '') === '/demo') url.pathname = '/'
  } else {
    url.searchParams.set('preset', preset)
  }
}

export function presetCameras<T extends { id: string }>(
  cameras: T[],
  name?: string,
  settings: CameraConfig = defaultConfig,
): T[] {
  const preset = settings.presets[resolvePreset(name, settings)]
  if (preset.includeLocations) {
    const byId = new Map(cameras.map((camera) => [camera.id, camera]))
    return preset.includeLocations.flatMap((id) => {
      const camera = byId.get(id)
      return camera ? [camera] : []
    })
  }
  return cameras.filter((camera) => !preset.excludeLocations?.includes(camera.id))
}

// Identity, names, ordering, and source assignments come from the API.
export function playerCameras(
  cameras: Camera[],
  settings: CameraConfig = defaultConfig,
): PlayerCamera[] {
  return cameras.map((camera) => ({
    ...camera,
    sourceIds: camera.sourceIds ?? [camera.id],
    view: settings.locations.find((location) => location.id === camera.id)?.view,
  }))
}

export function defaultCamera<T extends { id: string }>(cameras: T[]): T | undefined {
  return cameras[0]
}
